import { useMemo, useState } from 'react'
import * as ClientHttp from '../../../../../services/api/https/registration/client.http'
import * as SystemHttp from '../../../../../services/api/https/system/system.http'
import { getGeocode } from '../../../../../services/Maps'
import { errorHandler } from '../../../../../services/util/error.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  LOADING: 'Adquirindo informações',
  LOADING_NEW: 'Adquirindo informações para novo cadastro',
  LOADING_EDIT: 'Adquirindo informações para edição',
  LOADING_UF: 'Carregando unidades federativas',
  LOADING_CITIES: 'Carregando cidades',
  LOADING_COORDINATES: 'Adquirindo coordenadas do endereço',
  LOADING_CNPJ_DATA: 'Adquirindo dados do CNPJ',
  LOADING_CITY_COORDINATES: 'Adquirindo cidade e coordenadas',
  SAVE_FROM_MAP: 'Salvando cliente com mapa atualizado',
  SAVE_NEW: 'Salvando novo cliente',
  SAVE_NEW_ASSOC: 'Salvando novo cliente e associando a empresa',
  SAVE_UPDATE: 'Atualizando cliente',
  SUCCESS: 'Cliente cadastrado',
}

const INITIAL_CLIENTE = {
  razaoSocial: '',
  nomeFantasia: '',
  cnpj: '',
  address: '',
  district: '',
  number: '',
  makeParcialReturn: false,
  latitude: 0,
  longitude: 0,
  actived: false,
  city_id: 0,
  observation: '',
  phone: '',
  cep: '',
  inscricao_estadual: '',
}

const INITIAL_CICLO = {
  id: null,
}

const FIELD_ERRORS_MESSAGE = {
  razaoSocial: 'O campo Razão Social precisa ser preenchido',
  nomeFantasia: 'O campo Nome Fantasia precisa ser preenchido',
  cnpj: 'O campo CNPJ precisa ser preenchido',
  address: 'O campo Endereço precisa ser preenchido',
  district: 'O campo Bairro precisa ser preenchido',
  number: 'O campo Numero precisa ser preenchido',
  city_id: 'Uma cidade precisa ser selecionada',
  latitude: "Clique no botão 'Encontrar' antes de salvar",
  longitude: "Clique no botão 'Encontrar' antes de salvar",
}

export const INITIAL_STATE = {
  cliente: { ...INITIAL_CLIENTE },
  ciclo: INITIAL_CICLO,
  ufs: [],
  selectedUf: 0,
  selectedCity: 0,
  cities: [],
  activeTab: 0,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  selectedCustomer: null,
}

export default function useModalCliente() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    const assignValuesToCustomer = (...args) => {
      return Object.assign({ ...INITIAL_CLIENTE }, ...args)
    }

    const throwError = fieldName =>
      setState(prevState => ({ ...prevState, errorMessage: FIELD_ERRORS_MESSAGE[fieldName] }))

    const fieldIsValid = field =>
      field && field !== 0 && (typeof field === 'string' ? field.trim() !== '' : true)

    const areFieldsValid = ({
      makeParcialReturn,
      actived,
      observation,
      cep,
      phone,
      inscricao_estadual,
      ...fields
    }) => {
      let fieldsNames = Object.keys(fields)
      return fieldsNames.every(fieldName => {
        if (!fieldIsValid(fields[fieldName])) {
          throwError(fieldName)
          return false
        }
        return true
      })
    }

    return {
      // Actions
      buildInfo(selectedCustomer) {
        if (!selectedCustomer)
          setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.LOADING_NEW }))
        else if (selectedCustomer.id)
          setState(prevState => ({
            ...prevState,
            cliente: selectedCustomer,
            loadingMessage: LOAD_MESSAGE.LOADING_EDIT,
          }))
        else
          setState(prevState => ({
            ...prevState,
            cliente: selectedCustomer,
            loadingMessage: LOAD_MESSAGE.LOADING_NEW,
          }))
      },

      selectSaveType(selectedCustomer, selectedCiclo, cliente, associateWithSelectedCycle = false) {
        if (areFieldsValid(cliente)) {
          if (selectedCustomer && selectedCustomer.id)
            setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.SAVE_UPDATE }))
          else
            setState(prevState => ({
              ...prevState,
              loadingMessage:
                associateWithSelectedCycle && selectedCiclo.cicloId
                  ? LOAD_MESSAGE.SAVE_NEW_ASSOC
                  : LOAD_MESSAGE.SAVE_NEW,
            }))
        }
      },

      saveUpdateCustomer(cliente) {
        ClientHttp.updateClient({
          ...cliente,
        })
          .then(customer =>
            setState(prevState => ({
              ...prevState,
              cliente: customer,
              loadingMessage: LOAD_MESSAGE.SUCCESS,
            })),
          )
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      saveNewCustomer(cliente, selectedTp) {
        ClientHttp.registerClient({ ...cliente }, selectedTp)
          .then(customer =>
            setState(prevState => ({
              ...prevState,
              cliente: customer,
              loadingMessage: LOAD_MESSAGE.SUCCESS,
            })),
          )
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      saveNewAndAssocCompany({ company_code, ...cliente }, selectedTp, selectedCiclo) {
        ClientHttp.registerClientAndAssociateCompany(
          { ...cliente },
          company_code,
          selectedCiclo.cicloId,
          selectedTp,
        )
          .then(customer =>
            setState(prevState => ({
              ...prevState,
              cliente: customer,
              loadingMessage: LOAD_MESSAGE.SUCCESS,
            })),
          )
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      fetchUFs(ufId = null) {
        SystemHttp.getUfs()
          .then(ufs => {
            if (ufId)
              setState(prevState => ({
                ...prevState,
                ufs: ufs,
                selectedUf: ufs.find(uf => uf.id === ufId),
                loadingMessage: LOAD_MESSAGE.LOADING_CITIES,
              }))
            else
              setState(prevState => ({ ...prevState, ufs: ufs, loadingMessage: LOAD_MESSAGE.NONE }))
          })
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      fetchCities(ufId, selectedCustomer = null) {
        SystemHttp.getCities(ufId)
          .then(cities => {
            if (selectedCustomer) {
              setState(prevState => ({
                ...prevState,
                cities: cities,
                selectedCity: cities.find(city => city.id === selectedCustomer.city_id),
                loadingMessage: LOAD_MESSAGE.NONE,
              }))
            } else
              setState(prevState => ({
                ...prevState,
                cities: cities,
                loadingMessage: LOAD_MESSAGE.NONE,
              }))
          })
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      fetchCoordinates(numero, endereco, bairro, cidade) {
        getGeocode(`${endereco}, ${numero}, ${bairro}, ${cidade.name}-${cidade.uf_id}, Brasil`)
          .then(res => {
            if (!res.lng) throw new Error('Endereço não encontrado')
            const { lat, lng } = res

            setState(prevState => ({
              ...prevState,
              cliente: { ...prevState.cliente, latitude: lat, longitude: lng },
              loadingMessage: LOAD_MESSAGE.NONE,
            }))
          })
          .catch(err => {
            console.log(err)

            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            }))
          })
      },

      fetchCnpjData(cnpj) {
        SystemHttp.getCnpjDataFocusNfe(cnpj)
          .then(async res => {
            setState(prevState => ({
              ...prevState,
              cliente: {
                ...prevState.cliente,
                razaoSocial: res.razao_social,
                nomeFantasia: res.razao_social,
                city_id: parseInt(res.endereco.codigo_ibge),
                address: res.endereco.logradouro,
                district: res.endereco.bairro,
                number: res.endereco.numero,
                actived: true,
              },
              selectedCity: {
                id: parseInt(res.endereco.codigo_ibge),
                name: res.endereco.nome_municipio,
              },
              selectedUf: prevState.ufs.find(uf => uf.uf === res.endereco.uf),
              selectedCustomer: {
                ...prevState.selectedCustomer,
                city_id: parseInt(res.endereco.codigo_ibge),
              },
              loadingMessage: LOAD_MESSAGE.LOADING_CITY_COORDINATES,
            }))
          })
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      resetState() {
        setState(prevState => ({
          ...prevState,
          ...INITIAL_STATE,
          selectedUf: prevState.selectedUf,
          selectedCity: prevState.selectedCity,
          ufs: prevState.ufs,
          cities: prevState.cities,
          cliente: prevState.cliente,
        }))
      },

      resetError() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      clearModalOnClose() {
        setState({ ...INITIAL_STATE })
      },

      saveClientWithFineTuning() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.SAVE_FROM_MAP,
        }))
      },

      // Events
      onInit(selectedCustomer, selectedCiclo) {
        setState(prevState => {
          prevState = { ...INITIAL_STATE }
          prevState.selectedCustomer = assignValuesToCustomer(selectedCustomer)
          prevState.ciclo = selectedCiclo
          prevState.loadingMessage = LOAD_MESSAGE.LOADING

          return { ...prevState }
        })
      },

      onInputChange(name, value) {
        setState(prevState => {
          prevState.cliente[name] = value
          return { ...prevState }
        })
      },

      onEstadoChange(uf) {
        setState(prevState => {
          prevState.selectedUf = uf
          prevState.loadingMessage = LOAD_MESSAGE.LOADING_CITIES
          return { ...prevState }
        })
      },

      onCityChange(name, value) {
        setState(prevState => {
          prevState.selectedCity = value
          prevState.cliente[name] = value.id
          return { ...prevState }
        })
      },

      onBtnSalvarClicked(
        selectedCustomer,
        selectedCiclo,
        cliente,
        associateWithSelectedCycle = false,
      ) {
        this.selectSaveType(selectedCustomer, selectedCiclo, cliente, associateWithSelectedCycle)
      },

      onTabChange(value) {
        setState(prevState => ({ ...prevState, activeTab: value }))
      },

      onSearchCoordinatesClick(e, selectedCity) {
        e.preventDefault()
        if (selectedCity !== 0 && selectedCity !== null && selectedCity !== undefined)
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.LOADING_COORDINATES,
          }))
        else setState(prevState => ({ ...prevState, errorMessage: FIELD_ERRORS_MESSAGE.city_id }))
      },

      onCoordinatesChange(coordinates) {
        setState(prevState => ({
          ...prevState,
          cliente: {
            ...prevState.cliente,
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          },
        }))
      },

      onCnpjChange(cnpj, selectedCustomer) {
        this.onInputChange('cnpj', cnpj)

        if (cnpj.length === 14 && !selectedCustomer.id)
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.LOADING_CNPJ_DATA,
          }))
      },
    }
  }, [])

  return { state, localDispatch }
}
